export const EMBED_MESSAGE_TYPE = 'dpshtrr-chatbot-resize';

const INLINE_CLOSED_HEIGHT = 72;
const INLINE_OPEN_HEIGHT = 600;
const FLOATING_OPEN_WIDTH = 440;
const FLOATING_OPEN_HEIGHT = 720;
/** Closed iframe size (button is 64px; extra room for ring/pulse/shadow). */
const FLOATING_CLOSED_FRAME = 100;
const FLOATING_CLOSED_ICON = 64;

let cachedInlineOpenHeight = null;

export function isEmbedded() {
  return window.parent !== window;
}

/**
 * Iframe width is often ~60–440px even on desktop, so matchMedia(max-width:768)
 * is wrong inside the embed and causes a fillViewport ↔ 440px resize loop
 * (looks like the chat rapidly opening/closing).
 */
function isMobileViewport() {
  if (isEmbedded()) {
    const screenW =
      window.screen?.availWidth ||
      window.screen?.width ||
      1024;
    return screenW <= 768;
  }
  return window.matchMedia('(max-width: 768px)').matches;
}

function getInlineOpenHeight() {
  const frameHeight = window.frameElement?.clientHeight;
  if (frameHeight && frameHeight > INLINE_CLOSED_HEIGHT + 20) {
    cachedInlineOpenHeight = frameHeight;
  }
  return cachedInlineOpenHeight || INLINE_OPEN_HEIGHT;
}

function getInlineClosedHeight() {
  const collapsed = document.querySelector('.chatbox-inline-collapsed');
  if (collapsed) {
    const measured = Math.ceil(collapsed.getBoundingClientRect().height);
    if (measured > 0) {
      return measured + 12;
    }
  }

  const icon = document.querySelector('.chatbox-icon-button-inline');
  if (icon) {
    const measured = Math.ceil(icon.getBoundingClientRect().height);
    if (measured > 0) {
      return measured + 12;
    }
  }

  return INLINE_CLOSED_HEIGHT;
}

export function getEmbedMode() {
  const params = new URLSearchParams(window.location.search);
  const explicit = params.get('embed');

  if (explicit === 'inline') {
    return 'inline';
  }

  if (explicit === 'floating' || explicit === '1' || explicit === 'true') {
    return 'floating';
  }

  if (isEmbedded()) {
    return 'inline';
  }

  return 'floating';
}

function getFloatingOpenSize() {
  const isMobile = isMobileViewport();
  const offset = isMobile ? 16 : 24;

  if (isEmbedded()) {
    if (isMobile) {
      return {
        width: null,
        height: null,
        fillViewport: true,
        offset
      };
    }

    return {
      width: FLOATING_OPEN_WIDTH,
      height: FLOATING_OPEN_HEIGHT,
      offset
    };
  }

  // Match .chatbox-window standalone CSS (440 x 720, clamped to viewport)
  return {
    width: isMobile ? window.innerWidth - offset * 2 : Math.min(FLOATING_OPEN_WIDTH, window.innerWidth - 32),
    height: isMobile ? window.innerHeight - offset * 2 : Math.min(FLOATING_OPEN_HEIGHT, window.innerHeight - 48)
  };
}

export function getEmbedDimensions(isOpen) {
  const mode = getEmbedMode();
  const isMobile = isMobileViewport();

  if (mode === 'inline') {
    const openHeight = getInlineOpenHeight();
    return {
      mode,
      width: null,
      height: isOpen ? openHeight : getInlineClosedHeight(),
      isOpen,
      isMobile,
      fillFrame: false
    };
  }

  const offset = isMobile ? 16 : 24;
  const iconSize = isMobile ? 56 : FLOATING_CLOSED_ICON;

  if (!isOpen) {
    if (isEmbedded()) {
      return {
        mode,
        width: FLOATING_CLOSED_FRAME,
        height: FLOATING_CLOSED_FRAME,
        offset,
        iconSize,
        isOpen,
        isMobile,
        fillFrame: false
      };
    }

    return {
      mode,
      width: FLOATING_CLOSED_FRAME,
      height: FLOATING_CLOSED_FRAME,
      offset,
      iconSize,
      isOpen,
      isMobile,
      fillFrame: false
    };
  }

  const openSize = getFloatingOpenSize();
  return {
    mode,
    width: openSize.width,
    height: openSize.height,
    offset: openSize.offset ?? offset,
    iconSize,
    isOpen,
    isMobile,
    fillFrame: isEmbedded(),
    fillViewport: Boolean(openSize.fillViewport)
  };
}

function applyTargets(styles, selectors) {
  selectors.forEach((selector) => {
    const el = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!el) return;
    Object.assign(el.style, styles);
  });
}

function applyInlineDocumentSize(height, isOpen) {
  applyTargets(
    {
      margin: '0',
      padding: '0',
      width: '100%',
      height: `${height}px`,
      minHeight: `${height}px`,
      maxHeight: `${height}px`,
      overflow: isOpen ? 'hidden' : 'visible',
      background: 'transparent',
      boxSizing: 'border-box'
    },
    [document.documentElement, document.body, '#root', '.App', '.chatbox-embed-root']
  );
}

function applyFloatingDocumentSize(width, height) {
  applyTargets(
    {
      margin: '0',
      padding: '0',
      width: `${width}px`,
      height: `${height}px`,
      minWidth: `${width}px`,
      minHeight: `${height}px`,
      maxWidth: `${width}px`,
      maxHeight: `${height}px`,
      overflow: 'hidden',
      background: 'transparent',
      boxSizing: 'border-box'
    },
    [document.documentElement, document.body, '#root', '.App', '.chatbox-embed-root']
  );
}

function applyFloatingFrameFill() {
  applyTargets(
    {
      margin: '0',
      padding: '0',
      width: '100%',
      height: '100%',
      minWidth: '100%',
      minHeight: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      overflow: 'visible',
      background: 'transparent',
      boxSizing: 'border-box'
    },
    [document.documentElement, document.body, '#root', '.App', '.chatbox-embed-root']
  );
}

function resizeParentFrame(dims) {
  try {
    const frame = window.frameElement;
    if (!frame) return;

    frame.style.overflow = 'hidden';
    frame.style.background = 'transparent';
    frame.style.transition = 'width 0.25s ease, height 0.25s ease';
    frame.style.height = `${dims.height}px`;
    frame.setAttribute('height', String(dims.height));

    if (dims.mode === 'floating' && typeof dims.width === 'number') {
      frame.style.width = `${dims.width}px`;
      frame.setAttribute('width', String(dims.width));
    }
  } catch (_) {
    // Cross-origin embeds rely on embed.js in the parent page.
  }
}

function notifyParent(dims) {
  if (!isEmbedded()) return;

  window.parent.postMessage(
    {
      type: EMBED_MESSAGE_TYPE,
      mode: dims.mode,
      width: dims.width,
      height: dims.height,
      isOpen: dims.isOpen,
      isMobile: dims.isMobile,
      fillViewport: dims.fillViewport,
      offset: dims.offset
    },
    '*'
  );
}

export function shouldApplyEmbedSizing() {
  if (isEmbedded()) return true;
  return new URLSearchParams(window.location.search).has('embed');
}

export function applyEmbedDimensions(isOpen) {
  if (!shouldApplyEmbedSizing()) return;

  const apply = () => {
    const dims = getEmbedDimensions(isOpen);
    const root = document.querySelector('.chatbox-embed-root');

    if (root) {
      root.dataset.embedMode = dims.mode;
      root.classList.toggle('is-embedded', isEmbedded());
      root.classList.toggle('is-open', isOpen);
    }

    document.body.classList.toggle('chatbox-embed-active', isEmbedded());
    document.body.style.background = isEmbedded() ? 'transparent' : '';
    document.body.style.minHeight = isEmbedded() ? '0' : '';

    if (dims.mode === 'inline') {
      applyInlineDocumentSize(dims.height, isOpen);
    } else if (dims.fillFrame) {
      applyFloatingFrameFill();
    } else {
      applyFloatingDocumentSize(dims.width, dims.height);
    }

    resizeParentFrame(dims);
    notifyParent(dims);
  };

  apply();

  if (!isOpen) {
    requestAnimationFrame(apply);
  }
}
